import type {
	CollectionQueryOptions,
	CoreApiError,
	DetailQueryOptions,
	IMyLQALicensedHotel,
	IMyLQALicensedHotelsParams,
	IMyLQALicensedHotelsResponse,
	MyLQABillingHotelOverview,
	MyLQAContract,
	MyLQAContractActivityLog,
	MyLQAContractCollection,
	MyLQAContractPeriodCollection,
	myLQAContractsQuerySchema,
	MyLQAContractsStatistics,
	PaginatedParams,
	PaginatedQueryOptions,
	ParamsType,
} from '@lqa-frontend/api-services';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
	useCollectionQuery,
	useDetailQuery,
	usePaginatedQuery,
} from '@lqa-frontend/api-services';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import type { z } from 'zod';

export function useContractsQuery(
	params?: UseContractQueryParams,
	options?: PaginatedQueryOptions<MyLQAContractCollection>
) {
	return usePaginatedQuery<MyLQAContractCollection>(
		endpoints.myLQA.contracts.index,
		params,
		options
	);
}

type UseContractQueryParams = PaginatedParams &
	z.infer<typeof myLQAContractsQuerySchema>;

export type ContractsExportParams = Partial<UseContractQueryParams>;

export async function fetchContractsExportCsv(
	params?: ContractsExportParams
): Promise<Blob> {
	const response = await coreApiClient.get(endpoints.myLQA.contracts.export, {
		params,
		responseType: 'blob',
	});
	return response.data as Blob;
}

type ContractsStatisticsQueryOptions = Omit<
	UseQueryOptions<
		AxiosResponse<MyLQAContractsStatistics>,
		AxiosError<CoreApiError>,
		MyLQAContractsStatistics
	>,
	'queryKey' | 'queryFn' | 'select'
>;

export function useContractsStatisticsQuery(
	options?: ContractsStatisticsQueryOptions
) {
	return useQuery<
		AxiosResponse<MyLQAContractsStatistics>,
		AxiosError<CoreApiError>,
		MyLQAContractsStatistics
	>({
		queryKey: coreApiQueryKey(endpoints.myLQA.contracts.statistics, 'get'),
		queryFn: async () =>
			coreApiClient.get<MyLQAContractsStatistics>(
				endpoints.myLQA.contracts.statistics
			),
		select: (response) => response.data,
		...options,
	});
}

export function useContractQuery(
	id?: string,
	options?:
		Omit<DetailQueryOptions<MyLQAContract>, 'queryKey' | 'queryFn'> | undefined
) {
	return useDetailQuery<MyLQAContract>(
		endpoints.myLQA.contracts.item,
		id,
		options
	);
}

export function useContractActivityLogsQuery(
	id?: string,
	options?:
		| Omit<
				DetailQueryOptions<MyLQAContractActivityLog[]>,
				'queryKey' | 'queryFn'
		  >
		| undefined
) {
	return useDetailQuery<MyLQAContractActivityLog[]>(
		endpoints.myLQA.contracts.activityLogs,
		id,
		options
	);
}

export function useContractPeriodsQuery(
	contractId: string,
	params?: UseContractPeriodsQueryParams,
	options?: CollectionQueryOptions<MyLQAContractPeriodCollection>
) {
	return useCollectionQuery<MyLQAContractPeriodCollection>(
		endpoints.myLQA.contracts.periods(contractId),
		params,
		options
	);
}

export function useLicensedHotelsQuery(
	contractId: string,
	params: IMyLQALicensedHotelsParams,
	options?: PaginatedQueryOptions<
		IMyLQALicensedHotel,
		IMyLQALicensedHotelsResponse
	>
) {
	return usePaginatedQuery<
		IMyLQALicensedHotel,
		IMyLQALicensedHotelsResponse,
		IMyLQALicensedHotelsParams
	>(endpoints.myLQA.contracts.licensedHotels(contractId), params, options);
}

export type UseContractPeriodsQueryParams = ParamsType;

export function useHotelContractsOverviewQuery(
	hotelId?: number,
	options?: Omit<
		DetailQueryOptions<MyLQABillingHotelOverview>,
		'queryKey' | 'queryFn'
	>
) {
	return useDetailQuery(
		endpoints.myLQA.contracts.hotelOverview,
		hotelId,
		options
	);
}
